// Logs — the read-back of what the gateway DID, in one place (UX3/UX4/UX20).
//
// Three lists that were three tabs inside Overview's "Activity" block and one
// admin-only page called "Errors & alerts". They answer the same kind of question —
// "what happened, and when" — so they are three PANELS of one page, one after the
// other rather than behind a tab bar (UX20): a tab bar hides two thirds of a log from
// somebody who is scanning for something and does not yet know which third it is in.
//
//   Feed      the audit feed: the REST seed merged with the live event bus. First,
//             because a log section that leads with the error list is an error page
//             wearing a new name.
//   Calls     the call log, same merge.
//   Failures  the aggregated failure feed (ST43): failed tasks, errored route
//             instances, errored streams, dropped nodes, plus the threshold alerts
//             an admin's metrics summary adds. One reading of the same history.
//
// Each is collapsible and remembers its own state, so a reader who only ever wants
// one of them folds the other two away once.
//
// The page is no longer admin-gated. Everything it reads — tasks, streams,
// clients, audit, calls — the gateway already scopes BY CALLER; the ONE
// process-wide source is the metrics summary behind the threshold alerts, and
// that block is admin-only here (and 403s into nothing for anyone else anyway).

import { useMemo } from 'react';
import { api } from '../api/client';
import { useResource } from '../lib/useResource';
import { useLiveReseed, callsSig, clientsSig, routeInstancesSig } from '../lib/useLiveReseed';
import { useStore } from '../store/store';
import { Loading, ErrorState, Badge, StatusBadge } from '../components/ui';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { DataTable, type Column } from '../components/DataTable';
import { fmt, mergeById } from '../lib/feedMerge';
import { saturationSeverity, vramHeadroomSeverity, severityTone } from '../lib/thresholds';
import type { AuditEvent, CallLog, Task, StreamSession, Client, MetricsSummary } from '../api/schemas';

export type ErrorSource = 'task' | 'route' | 'stream' | 'node';

export interface ErrorItem {
  source: ErrorSource;
  id: string;
  title: string;
  detail?: string;
  ts: number;
  tone: 'bad' | 'warn';
}

export interface ErrorInputs {
  failedTasks: Pick<Task, 'id' | 'canonical_tool' | 'error' | 'finished_at' | 'client_id'>[];
  routeErrors: { instance_id: string; route_name: string; version: number; error?: string; ts: number }[];
  erroredStreams: { id: string; error?: string; media_type?: string }[];
  offlineNodes: { id: string; name: string; last_seen?: string }[];
}

function toMs(ts?: string): number {
  if (!ts) return 0;
  const t = new Date(ts).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Pure aggregation across the four failure sources → one newest-first list.
export function aggregateErrors(inputs: ErrorInputs): ErrorItem[] {
  const items: ErrorItem[] = [];
  for (const t of inputs.failedTasks) {
    items.push({
      source: 'task',
      id: `task:${t.id}`,
      title: t.canonical_tool || t.id,
      detail: t.error?.message ?? 'task failed',
      ts: toMs(t.finished_at),
      tone: 'bad',
    });
  }
  for (const r of inputs.routeErrors) {
    items.push({
      source: 'route',
      id: `route:${r.instance_id}`,
      title: `${r.route_name} v${r.version}`,
      detail: r.error ?? 'route instance errored',
      ts: r.ts,
      tone: 'bad',
    });
  }
  for (const s of inputs.erroredStreams) {
    items.push({
      source: 'stream',
      id: `stream:${s.id}`,
      title: s.id,
      detail: s.error ?? `stream errored${s.media_type ? ` (${s.media_type})` : ''}`,
      tone: 'bad',
      ts: 0,
    });
  }
  for (const n of inputs.offlineNodes) {
    items.push({
      source: 'node',
      id: `node:${n.id}`,
      title: n.name,
      detail: `node offline${n.last_seen ? ` — last seen ${new Date(n.last_seen).toLocaleString()}` : ''}`,
      tone: 'warn',
      ts: toMs(n.last_seen),
    });
  }
  return items.sort((a, b) => b.ts - a.ts);
}

const SOURCE_LABEL: Record<ErrorSource, string> = {
  task: 'task',
  route: 'route',
  stream: 'stream',
  node: 'node',
};

function Failures() {
  const failed = useResource(() => api.tasks({ state: 'FAILED' }), []);
  const streamsRes = useResource(() => api.streams(), []);
  const clientsRes = useResource(() => api.clients(), []);
  const summaryRes = useResource(() => api.metricsSummary(300).catch(() => null), []);
  useLiveReseed(clientsRes.reload, clientsSig);

  // Live overlays.
  const liveRoutes = useStore((s) => s.routeInstances);
  const liveClients = useStore((s) => s.clients);
  useLiveReseed(() => {}, routeInstancesSig); // subscribe so the memo recomputes on route.errored

  const items = useMemo(() => {
    const failedTasks = failed.data?.items ?? [];
    const routeErrors = Object.values(liveRoutes)
      .filter((r) => r.status === 'errored')
      .map((r) => ({ instance_id: r.instance_id, route_name: r.route_name, version: r.version, error: r.error, ts: r.ts }));
    // Streams errored per the REST seed (the bus overlay only carries ACTIVE/CLOSED,
    // so a CLOSED stream that the seed still marks ERRORED stays flagged).
    const seedStreams: StreamSession[] = streamsRes.data?.items ?? [];
    const erroredStreams = seedStreams
      .filter((s) => s.status === 'ERRORED')
      .map((s) => ({ id: s.id, media_type: s.media_type }));
    // Offline nodes: REST seed reconciled with the live client.status overlay.
    const offline = new Map<string, { id: string; name: string; last_seen?: string }>();
    for (const c of (clientsRes.data?.items ?? []) as Client[]) {
      const live = liveClients[c.id];
      const status = live?.status ?? c.status;
      if (status === 'offline') offline.set(c.id, { id: c.id, name: c.name, last_seen: c.last_seen });
    }
    return aggregateErrors({ failedTasks, routeErrors, erroredStreams, offlineNodes: [...offline.values()] });
  }, [failed.data, streamsRes.data, clientsRes.data, liveRoutes, liveClients]);

  const alerts = useMemo(() => buildThresholdAlerts(summaryRes.data ?? null), [summaryRes.data]);

  if (failed.loading && !failed.data) return <Loading what="Loading errors…" />;

  const counts = items.reduce<Record<string, number>>((a, i) => ({ ...a, [i.source]: (a[i.source] ?? 0) + 1 }), {});

  return (
    <>
      <div className="muted-row small" data-testid="failure-counts">
        {(['task', 'route', 'stream', 'node'] as ErrorSource[]).map((s) => (
          <span key={s} className="chip">{SOURCE_LABEL[s]} {counts[s] ?? 0}</span>
        ))}
      </div>

      {failed.error && <ErrorState message={failed.error} onRetry={failed.reload} />}

      {alerts.length > 0 && (
        <section className="card">
          <header className="card-head"><h2>Threshold alerts</h2></header>
          <div className="card-body">
            <ul className="feed">
              {alerts.map((a) => (
                <li key={a.id} className="feed-row">
                  <Badge tone={severityTone[a.severity]}>{a.kind}</Badge>
                  <code className="mono small">{a.instance}</code>
                  <span className="muted">{a.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="card">
        <header className="card-head"><h2>Recent failures</h2></header>
        <div className="card-body">
          {items.length === 0 ? (
            <div className="muted small">No failures across tasks, routes, streams or nodes. 🎉</div>
          ) : (
            <ul className="feed">
              {items.map((i) => (
                <li key={i.id} className="feed-row">
                  <span className="feed-ts">{i.ts ? new Date(i.ts).toLocaleTimeString() : '—'}</span>
                  <Badge tone={i.tone}>{i.source}</Badge>
                  <code className="mono small">{i.title}</code>
                  {i.detail && <span className="muted">{i.detail}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

// --- the page: three reads of the same history, stacked ---------------------

/** The panels, in the order they are drawn. Exported so the order is assertable —
 *  it is the whole point of UX20 that the feed comes first and nothing is hidden
 *  behind a tab. */
export const LOGS_PANELS = ['feed', 'calls', 'failures'] as const;
export type LogsPanel = (typeof LOGS_PANELS)[number];

export function Logs() {
  return (
    <div className="view">
      <div className="view-head">
        <h1>Logs</h1>
      </div>

      <CollapsibleSection title="Feed" storageKey="logs.collapsed:feed">
        <AuditFeed />
      </CollapsibleSection>

      <CollapsibleSection title="Calls" storageKey="logs.collapsed:calls">
        <CallsLog />
      </CollapsibleSection>

      <CollapsibleSection title="Failures" storageKey="logs.collapsed:failures">
        <Failures />
      </CollapsibleSection>
    </div>
  );
}

// The audit feed: the REST seed merged with the live bus projection. The frozen bus
// catalog has no audit.* type — the store synthesizes the feed from the typed frames
// (task/stream/publication/client/object changes).
function AuditFeed() {
  const seed = useResource(() => api.audit(), []);
  const live = useStore((s) => s.feed);
  const merged = useMemo(
    () => mergeById<AuditEvent>(live, seed.data?.items ?? [], (a) => a.id),
    [live, seed.data],
  );
  if (seed.loading) return <Loading />;
  if (seed.error) return <ErrorState message={seed.error} onRetry={seed.reload} />;
  if (merged.length === 0) return <div className="muted small">Nothing recorded yet.</div>;
  return (
    <ul className="feed">
      {merged.map((a) => (
        <li key={a.id} className="feed-row">
          <span className="feed-ts">{fmt(a.ts)}</span>
          <span className="chip">{a.action}</span>
          <span className="muted">{a.actor ?? 'system'}</span>
          {a.target && <code className="mono small">{a.target}</code>}
        </li>
      ))}
    </ul>
  );
}

function CallsLog() {
  const seed = useResource(() => api.calls(), []);
  const live = useStore((s) => s.calls);
  // Realtime: the live store already prepends new calls; this also re-syncs the REST
  // seed when a fresh call.logged arrives so the full list stays current.
  useLiveReseed(seed.reload, callsSig);
  const merged = useMemo(
    () => mergeById<CallLog>(live, seed.data?.items ?? [], (c) => c.id),
    [live, seed.data],
  );
  const cols: Column<CallLog>[] = [
    { key: 'ts', header: 'Time', cell: (c) => <span className="muted">{fmt(c.ts)}</span> },
    { key: 'tool', header: 'Tool', cell: (c) => <code className="mono">{c.canonical_tool}</code> },
    { key: 'state', header: 'State', cell: (c) => <StatusBadge status={c.state} /> },
    { key: 'dur', header: 'Duration', align: 'right', cell: (c) => (c.duration_ms != null ? `${(c.duration_ms / 1000).toFixed(1)}s` : '—') },
    { key: 'gpu', header: 'GPU·s', align: 'right', cell: (c) => c.usage?.gpu_seconds?.toFixed(1) ?? '—' },
  ];
  if (seed.loading) return <Loading />;
  if (seed.error) return <ErrorState message={seed.error} onRetry={seed.reload} />;
  return <DataTable columns={cols} rows={merged} rowKey={(c) => c.id} empty="No calls." />;
}

interface ThresholdAlert {
  id: string;
  kind: 'CREDIT_SATURATION' | 'VRAM_HEADROOM';
  instance: string;
  detail: string;
  severity: 'warn' | 'bad';
}

export function buildThresholdAlerts(summary: MetricsSummary | null): ThresholdAlert[] {
  if (!summary) return [];
  const out: ThresholdAlert[] = [];
  for (const s of summary.saturation) {
    const sev = saturationSeverity(s.value);
    if (sev !== 'ok') {
      const pct = Math.round(s.value > 1 ? s.value : s.value * 100);
      out.push({ id: `sat:${s.instance}`, kind: 'CREDIT_SATURATION', instance: s.instance, detail: `${pct}% credits used`, severity: sev });
    }
  }
  for (const v of summary.vram_headroom_mb) {
    const sev = vramHeadroomSeverity(v.free_mb);
    if (sev !== 'ok') {
      out.push({ id: `vram:${v.instance}`, kind: 'VRAM_HEADROOM', instance: v.instance, detail: `${(v.free_mb / 1024).toFixed(1)} GB free`, severity: sev });
    }
  }
  return out;
}
